import {
  Component,
  DestroyRef,
  inject,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  signal,
  computed,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TranslatePipe } from '@ngx-translate/core';

import { ProductService } from '../../../core/services/product.service';
import { MoneyPipe } from '../../pipes/money.pipe';
import { ProductVariantMatrixDto, ProductVariantDto } from '../../../core/models/variant.models';

/** Sotuvchiga variant tanlash matritsasini ko'rsatadigan dialog (talab #8). */
export interface SelectedVariant {
  productId: string;
  variantId: string;
  sku: string;
  label: string;
  price: number;
  stock: number;
}

@Component({
  selector: 'app-variant-picker',
  standalone: true,
  imports: [CommonModule, DialogModule, ButtonModule, TagModule, TranslatePipe, MoneyPipe],
  templateUrl: './variant-picker.component.html',
})
export class VariantPickerComponent implements OnChanges {
  private productSvc = inject(ProductService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() visible = false;
  @Input() productId: string | null = null;
  @Input() storeId: string | null = null;
  /**
   * Variantlar mahsulotning sotuv valyutasini meros oladi — DTO da alohida
   * valyuta yo'q. Berilmasa tenant kotirovka valyutasi ishlatiladi.
   */
  @Input() sellCurrency: string | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() variantSelected = new EventEmitter<SelectedVariant>();

  loading = signal(false);
  matrix = signal<ProductVariantMatrixDto | null>(null);

  /** Har bir o'q (atribut) uchun tanlangan qiymat: attributeId → valueId. */
  selected = signal<Record<string, string>>({});

  ngOnChanges(changes: SimpleChanges) {
    if ((changes['visible'] || changes['productId']) && this.visible && this.productId) {
      this.load(this.productId);
    }
  }

  private load(productId: string) {
    this.loading.set(true);
    this.matrix.set(null);
    this.selected.set({});
    this.productSvc
      .getVariantMatrix(productId, this.storeId ?? undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const m = res.data ?? null;
          this.matrix.set(m);
          // Bitta o'q/variant bo'lsa avtomatik tanlaymiz (oddiy mahsulot — default variant)
          if (m && m.attributes.length === 0 && m.variants.length === 1) {
            this.confirmVariant(m.variants[0]);
          }
        },
        error: () => this.loading.set(false),
        complete: () => this.loading.set(false),
      });
  }

  selectValue(attributeId: string, valueId: string) {
    this.selected.update((s) => {
      const next = { ...s };
      if (next[attributeId] === valueId) delete next[attributeId];
      else next[attributeId] = valueId;
      return next;
    });
  }

  isSelected(attributeId: string, valueId: string): boolean {
    return this.selected()[attributeId] === valueId;
  }

  /** Tanlangan kombinatsiyaga mos variant (barcha o'qlar tanlanganda). */
  matchedVariant = computed<ProductVariantDto | null>(() => {
    const m = this.matrix();
    if (!m) return null;
    const sel = this.selected();
    if (Object.keys(sel).length !== m.attributes.length) return null;
    const wanted = new Set(Object.values(sel));
    return (
      m.variants.find(
        (v) => v.values.length === wanted.size && v.values.every((vv) => wanted.has(vv.valueId)),
      ) ?? null
    );
  });

  /**
   * Berilgan qiymat uchun mavjud qoldiq: shu qiymatni o'z ichiga olgan va
   * boshqa o'qlardagi joriy tanlovga mos variantlar qoldig'i yig'indisi.
   */
  stockForValue(attributeId: string, valueId: string): number | null {
    const m = this.matrix();
    if (!m) return null;
    const sel = this.selected();
    let total = 0;
    let known = false;
    for (const v of m.variants) {
      const ids = v.values.map((x) => x.valueId);
      if (!ids.includes(valueId)) continue;
      // Boshqa o'qlardagi tanlovga mos kelishi kerak
      const ok = Object.entries(sel).every(
        ([axisId, vid]) => axisId === attributeId || ids.includes(vid),
      );
      if (!ok) continue;
      if (v.stock != null) {
        total += v.stock;
        known = true;
      }
    }
    return known ? total : null;
  }

  confirm() {
    const v = this.matchedVariant();
    if (v) this.confirmVariant(v);
  }

  private confirmVariant(v: ProductVariantDto) {
    const m = this.matrix();
    if (!m) return;
    const label = v.values.map((x) => x.value).join(' / ') || m.name;
    this.variantSelected.emit({
      productId: m.productId,
      variantId: v.id,
      sku: v.sku,
      label,
      price: v.effectiveSellPrice,
      stock: v.stock ?? 0,
    });
    this.close();
  }

  close() {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
